import { AlertTriangle, CheckCircle2 } from "lucide-react";

import { cn } from "@lib/cn.ts";
import type { AvailabilityState } from "@lib/types.ts";

interface Props {
  states: Record<string, AvailabilityState> | undefined;
  className?: string;
}

/**
 * Deployment reality for one operation.
 *
 * Populated by `pnpm spec:verify`, which diffs the authored spec against each
 * environment's live springdoc document. It exists because documented and
 * deployed are not the same thing: an endpoint can be merged, specified and
 * reviewed while still being absent from production, and a partner who cannot
 * see that difference wastes a day on a 404.
 */
export function AvailabilityBadge({ states, className }: Props) {
  if (!states) return null;

  const absent = Object.entries(states)
    .filter(([, state]) => state === "absent")
    .map(([env]) => env);

  const live = Object.entries(states)
    .filter(([, state]) => state === "live")
    .map(([env]) => env);

  if (absent.length === 0) {
    if (live.length === 0) return null;
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-sm2 bg-success-tint px-2 py-0.5",
          "text-[11.5px] font-medium text-success",
          className,
        )}
      >
        <CheckCircle2 className="size-3.5" aria-hidden />
        Available in {live.join(", ")}
      </span>
    );
  }

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-sm2 bg-warning-tint px-2 py-0.5",
        "text-[11.5px] font-medium text-warning",
        className,
      )}
    >
      <AlertTriangle className="size-3.5" aria-hidden />
      Not yet deployed to {absent.join(", ")}
      {live.length > 0 ? ` — available in ${live.join(", ")}` : null}
    </span>
  );
}
